/**
 * iOS Settings-like inline form fields.
 * Compact, native mobile appearance.
 */

import { useState, useRef, useEffect, useMemo, type ReactNode } from "react";
import { IconChevronRight } from "../../../../components/Icons.tsx";
import { formatDateForInputBangkok } from "../../../../lib/bangkok-time.ts";
import { haptic } from "../../../../lib/telegram.ts";
import { sanitizeUrl } from "../../../../lib/url-utils.ts";

// ============================================================================
// Section Label
// ============================================================================

interface SectionLabelProps {
  children: ReactNode;
}

export function SectionLabel({ children }: SectionLabelProps) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2 px-1">
      {children}
    </p>
  );
}

// ============================================================================
// Inline Text Field
// ============================================================================

interface InlineTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: "text" | "url" | "number";
  labelWidth?: string;
  required?: boolean;
  disabled?: boolean;
  mono?: boolean;
}

export function InlineTextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  labelWidth = "w-28",
  required,
  disabled,
  mono,
}: InlineTextFieldProps) {
  return (
    <div className="flex items-center min-h-[52px] px-4 border-b border-slate-100 last:border-b-0">
      <span
        className={`text-[15px] text-slate-500 ${labelWidth} flex-shrink-0`}
      >
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`flex-1 text-[15px] bg-transparent outline-none text-right placeholder:text-slate-300 disabled:text-slate-400 ${
          mono ? "font-mono" : ""
        } ${value ? "text-slate-900" : "text-slate-400"}`}
      />
    </div>
  );
}

// ============================================================================
// Inline Text Area (multiline, auto-resize)
// ============================================================================

interface InlineTextAreaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  required?: boolean;
}

export function InlineTextArea({
  label,
  value,
  onChange,
  placeholder,
  minRows = 1,
  maxRows = 4,
  required,
}: InlineTextAreaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to calculate new height
    textarea.style.height = "auto";

    // Calculate line height
    const lineHeight = 24; // ~1.5rem for text-[15px]
    const minHeight = lineHeight * minRows;
    const maxHeight = lineHeight * maxRows;

    // Set new height
    const newHeight = Math.min(
      Math.max(textarea.scrollHeight, minHeight),
      maxHeight
    );
    textarea.style.height = `${newHeight}px`;
  }, [value, minRows, maxRows]);

  return (
    <div className="px-4 py-3 border-b border-slate-100 last:border-b-0">
      <label className="block text-[15px] text-slate-500 mb-2">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={minRows}
        className="w-full text-[15px] text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 outline-none resize-none placeholder:text-slate-300 leading-relaxed"
        style={{ minHeight: `${24 * minRows + 20}px` }}
      />
    </div>
  );
}

// ============================================================================
// Inline Text Preview (opens fullscreen editor)
// ============================================================================

interface InlineTextPreviewProps {
  label: string;
  value: string;
  placeholder?: string;
  onClick: () => void;
  labelWidth?: string;
  maxLength?: number;
}

export function InlineTextPreview({
  label,
  value,
  placeholder = "Добавить...",
  onClick,
  maxLength = 60,
}: InlineTextPreviewProps) {
  const displayValue = value
    ? value.length > maxLength
      ? value.slice(0, maxLength) + "..."
      : value.replace(/\n/g, " ")
    : "";

  return (
    <button
      type="button"
      onClick={() => {
        haptic("light");
        onClick();
      }}
      className="w-full px-4 py-3 border-b border-slate-100 last:border-b-0 active:bg-slate-50 transition-colors text-left"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[13px] text-slate-400">{label}</span>
        <IconChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
      </div>
      <p
        className={`text-[15px] leading-snug ${
          value ? "text-slate-900" : "text-slate-300"
        }`}
      >
        {displayValue || placeholder}
      </p>
    </button>
  );
}

// ============================================================================
// Inline Date Time Field (native datetime-local input)
// ============================================================================

interface InlineDateTimeFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  labelWidth?: string;
  required?: boolean;
}

const defaultDatetimeLocal = () =>
  formatDateForInputBangkok(new Date().toISOString());

export function InlineDateTimeField({
  label,
  value,
  onChange,
  labelWidth = "w-28",
  required,
}: InlineDateTimeFieldProps) {
  const inputValue = value || (required ? defaultDatetimeLocal() : "");

  return (
    <div className="flex items-center min-h-[52px] px-4 border-b border-slate-100 last:border-b-0">
      <span
        className={`text-[15px] text-slate-500 ${labelWidth} flex-shrink-0`}
      >
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      <input
        type="datetime-local"
        value={inputValue}
        onChange={(e) => {
          haptic("light");
          onChange(e.target.value);
        }}
        className="flex-1 text-[15px] bg-transparent outline-none text-right text-slate-900 min-w-0 [color-scheme:light]"
      />
    </div>
  );
}

// ============================================================================
// Inline Stepper (for numbers with +/- buttons and direct input)
// ============================================================================

interface InlineStepperProps {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  placeholder?: string;
  labelWidth?: string;
}

export function InlineStepper({
  label,
  value,
  onChange,
  min = 0,
  max = 9999,
  step = 1,
  defaultValue,
  placeholder = "Нет",
  labelWidth = "w-28",
}: InlineStepperProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDecrement = () => {
    haptic("light");
    if (value === null) {
      // If null and has default, set to default
      if (defaultValue !== undefined) {
        onChange(defaultValue);
      } else {
        onChange(min);
      }
    } else if (value - step >= min) {
      onChange(value - step);
    } else {
      onChange(null);
    }
  };

  const handleIncrement = () => {
    haptic("light");
    if (value === null) {
      // If null, set to default or min + step
      if (defaultValue !== undefined) {
        onChange(defaultValue);
      } else {
        onChange(min + step);
      }
    } else if (value + step <= max) {
      onChange(value + step);
    }
  };

  const handleStartEditing = () => {
    haptic("light");
    setInputValue(value !== null ? String(value) : "");
    setIsEditing(true);
  };

  const handleFinishEditing = () => {
    setIsEditing(false);
    if (inputValue.trim() === "") {
      onChange(null);
    } else {
      const num = parseInt(inputValue, 10);
      if (!isNaN(num)) {
        onChange(Math.max(min, Math.min(max, num)));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleFinishEditing();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  return (
    <div className="flex items-center min-h-[52px] px-4 border-b border-slate-100 last:border-b-0">
      <span
        className={`text-[15px] text-slate-500 ${labelWidth} flex-shrink-0`}
      >
        {label}
      </span>
      <div className="flex-1 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleDecrement}
          className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 text-xl font-medium active:bg-slate-200 transition-colors flex items-center justify-center"
        >
          −
        </button>

        {isEditing ? (
          <input
            ref={inputRef}
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleFinishEditing}
            onKeyDown={handleKeyDown}
            min={min}
            max={max}
            className="w-20 h-9 text-center text-[15px] font-medium text-slate-900 bg-white border border-slate-200 rounded-xl outline-none focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)]"
          />
        ) : (
          <button
            type="button"
            onClick={handleStartEditing}
            className="w-20 h-9 text-center text-[15px] font-medium text-slate-900 bg-white border border-slate-200 rounded-xl active:bg-slate-50 transition-colors"
          >
            {value !== null ? value : placeholder}
          </button>
        )}

        <button
          type="button"
          onClick={handleIncrement}
          className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 text-xl font-medium active:bg-slate-200 transition-colors flex items-center justify-center"
        >
          +
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Inline Toggle
// ============================================================================

interface InlineToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  color?: "green" | "amber" | "blue" | "purple";
}

export function InlineToggle({
  label,
  description,
  checked,
  onChange,
  color = "green",
}: InlineToggleProps) {
  const colorClasses = {
    green: "bg-green-500",
    amber: "bg-amber-500",
    blue: "bg-blue-500",
    purple: "bg-purple-500",
  };

  return (
    <div className="flex items-center justify-between min-h-[52px] px-4 border-b border-slate-100 last:border-b-0">
      <div className="flex-1 min-w-0 pr-3">
        <span className="text-[15px] text-slate-900">{label}</span>
        {description && (
          <p className="text-[12px] text-slate-500 mt-0.5">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          haptic("light");
          onChange(!checked);
        }}
        className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
          checked ? colorClasses[color] : "bg-slate-200"
        }`}
      >
        <span
          className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
            checked ? "left-5" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}

// ============================================================================
// Inline Picker Row (expandable)
// ============================================================================

interface InlinePickerRowProps {
  label: string;
  value: string;
  onClick: () => void;
  badge?: ReactNode;
  labelWidth?: string;
  isOpen?: boolean;
}

export function InlinePickerRow({
  label,
  value,
  onClick,
  badge,
  labelWidth = "w-28",
  isOpen = false,
}: InlinePickerRowProps) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic("light");
        onClick();
      }}
      className={`w-full flex items-center min-h-[52px] px-4 border-b border-slate-100 last:border-b-0 active:bg-slate-50 transition-colors text-left ${
        isOpen ? "bg-slate-50" : ""
      }`}
    >
      <span
        className={`text-[15px] text-slate-500 ${labelWidth} flex-shrink-0`}
      >
        {label}
      </span>
      <div className="flex-1 flex items-center justify-end gap-2">
        {badge}
        <span
          className={`text-[15px] ${
            isOpen ? "text-[var(--color-accent)] font-medium" : "text-slate-900"
          }`}
        >
          {value}
        </span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? "rotate-180 text-[var(--color-accent)]" : "text-slate-400"
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </button>
  );
}

// ============================================================================
// Expandable Picker Options
// ============================================================================

interface PickerOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  badge?: ReactNode;
}

interface ExpandablePickerProps<T extends string> {
  isOpen: boolean;
  options: PickerOption<T>[];
  value: T;
  onChange: (value: T) => void;
  onClose: () => void;
}

export function ExpandablePicker<T extends string>({
  isOpen,
  options,
  value,
  onChange,
  onClose,
}: ExpandablePickerProps<T>) {
  if (!isOpen) return null;

  return (
    <div className="mx-3 mb-2 rounded-xl overflow-hidden bg-white border border-slate-200 shadow-sm">
      {options.map((option, index) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              haptic("light");
              onChange(option.value);
              onClose();
            }}
            className={`w-full flex items-center justify-between min-h-[48px] px-4 transition-colors ${
              index < options.length - 1 ? "border-b border-slate-100" : ""
            } ${isSelected ? "bg-slate-50" : "active:bg-slate-50"}`}
          >
            <div className="flex items-center gap-3">
              {option.badge}
              <div className="text-left">
                <span
                  className={`text-[14px] ${
                    isSelected
                      ? "font-semibold text-[var(--color-accent)]"
                      : "font-medium text-slate-700"
                  }`}
                >
                  {option.label}
                </span>
                {option.description && (
                  <p className="text-[12px] text-slate-500">
                    {option.description}
                  </p>
                )}
              </div>
            </div>
            {isSelected && (
              <svg
                className="w-5 h-5 text-[var(--color-accent)] flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Inline Link Field (with external link button and hint)
// ============================================================================

interface InlineLinkFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
}

export function InlineLinkField({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: InlineLinkFieldProps) {
  // Sanitize URL for safe rendering in href attribute
  const safeUrl = useMemo(() => sanitizeUrl(value), [value]);

  return (
    <div className="px-4 py-3 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[15px] text-slate-500`}>{label}</span>
        {hint && <span className="text-[12px] text-slate-400">· {hint}</span>}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 text-[15px] bg-slate-50 rounded-lg px-3 py-2 outline-none placeholder:text-slate-300 ${
            value ? "text-slate-900" : "text-slate-400"
          }`}
        />
        {safeUrl && (
          <a
            href={safeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 active:bg-slate-200 transition-colors flex-shrink-0"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
              />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
}
